import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import { colors } from '@/shared/tokens/colors';
import { getColorForUsecaseStatus } from '../HomepageTableView';
import { getLanguageName } from '@/shared/utils/helpers';
/**
 * Shared helpers used by both the TaskCard (Card View) and the TaskRow
 * (List View) to summarize a sub_group of usecases that compose one Task.
 *
 * Keeping these pure and side-effect free so the card and the row stay
 * in lock-step without duplicating logic.
 */

export const getCountedLabels = (items, getLabel) => {
    const counts = new Map();
    (items || []).forEach((item) => {
        const label = getLabel(item);
        if (!label) return;
        counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
};

export const countByLabel = (items, getLabel) =>
    getCountedLabels(items, getLabel)
        .map(({ label, count }) => `${count} ${label}`)
        .join(', ');

export const getCurrentVersionLabel = (usecases) => {
    const versions = (usecases || [])
        .map((usecase) => Number(usecase?.version))
        .filter(Number.isFinite);

    if (versions.length === 0) return '';

    return `V${Math.max(...versions)}`;
};

export const getEarliestCreatedAt = (usecases) =>
    (usecases || []).reduce((earliest, usecase) => {
        const createdAt = usecase?.created_datetime;
        if (!createdAt) return earliest;
        return !earliest || new Date(createdAt) < new Date(earliest) ? createdAt : earliest;
    }, null);

export const getLatestUpdatedAt = (usecases) =>
    (usecases || []).reduce((latest, usecase) => {
        const updatedAt = usecase?.last_update_time;
        if (!updatedAt) return latest;
        return !latest || new Date(updatedAt) > new Date(latest) ? updatedAt : latest;
    }, null);

export const getLanguagesText = (usecases) => {
    const languageCodes = (usecases || [])
        .flatMap((usecase) => usecase?.task_languages || [])
        .filter(Boolean);
    const uniqueLanguageCodes = Array.from(new Set(languageCodes));
    if (uniqueLanguageCodes.length === 0) return '';
    return uniqueLanguageCodes.map((language) => getLanguageName(language)).join(', ');
};

// Compact label for a list of distinct deployed versions. Keeps the badge
// readable when sub-tasks diverge: 1 → "V2", 2-3 → "V1, V2", 4+ → "Mixed
// versions" (full breakdown lives in the hover tooltip).
const MAX_INLINE_VERSIONS = 3;
const formatDeployedVersionsLabel = (versions) => {
    if (versions.length === 0) return null;
    if (versions.length <= MAX_INLINE_VERSIONS) {
        return versions.map((v) => `V${v}`).join(', ');
    }
    return 'Mixed versions';
};

export const getDeploymentBadge = (usecases) => {
    const list = usecases || [];
    const inProgressUsecase = list.find((usecase) =>
        [deploy_in_progress, undeploy_in_progress].includes(usecase?.status)
    );
    if (inProgressUsecase) {
        return {
            label: inProgressUsecase.status,
            color: getColorForUsecaseStatus(inProgressUsecase.status),
        };
    }

    const deployedUsecases = list.filter((usecase) => usecase?.is_deployed);
    if (deployedUsecases.length === 0) return null;

    const distinctVersions = Array.from(
        new Set(
            deployedUsecases
                .map((usecase) => usecase?.deployed_version)
                .filter((v) => v !== null && v !== undefined && v !== '')
        )
    ).sort((a, b) => Number(a) - Number(b));

    const versionsLabel = formatDeployedVersionsLabel(distinctVersions);
    const label = versionsLabel ? `${versionsLabel} deployed` : 'Deployed';

    // Only attach a tooltip breakdown when the inline label can't fully
    // tell the story — multiple deployed versions, or some sub-tasks
    // deployed and others not. For uniform deployments the label is
    // already complete, so we keep the badge tooltip-free (no hover noise).
    const hasMixedVersions = distinctVersions.length > 1;
    const hasUndeployedSiblings =
        list.length > 1 && deployedUsecases.length < list.length;
    const showBreakdown = hasMixedVersions || hasUndeployedSiblings;

    const breakdown = showBreakdown
        ? list.map((usecase) => ({
              name: usecase?.usecase_name || 'sub-task',
              deployed: !!usecase?.is_deployed,
              versionLabel: usecase?.is_deployed
                  ? usecase?.deployed_version
                      ? `V${usecase.deployed_version} deployed`
                      : 'Deployed'
                  : 'Not deployed',
          }))
        : null;

    return {
        label,
        color: colors.green,
        breakdown,
    };
};

/**
 * Turns the deployment badge computed by `getDeploymentBadge` into the
 * Figma tag props: "Deployed (V5)" success tag for deployed tasks, or a
 * processing tag while a deploy/undeploy is in flight. Shared by the
 * homepage TaskCard and the task page header.
 */
export const deploymentTagProps = (badge) => {
    if (!badge?.label) return null;
    if (badge.label === 'Deployed') {
        return { variant: 'success', label: 'Deployed' };
    }
    if (badge.label.endsWith(' deployed')) {
        const versions = badge.label.slice(0, -' deployed'.length);
        return { variant: 'success', label: `Deployed (${versions})` };
    }
    // Deploy/undeploy in progress — badge label is the raw status.
    return { variant: 'processing', label: badge.label };
};

export const getTaskTitle = (subGroup) => {
    const usecases = subGroup?.usecases || [];
    return (
        (subGroup?.sub_group_name || '').trim() ||
        usecases[0]?.usecase_name ||
        'Task'
    );
};

export const getTaskDescription = (subGroup) => {
    const usecases = subGroup?.usecases || [];
    return usecases[0]?.usecase_goal || 'No task description provided.';
};

/**
 * Returns true when every usecase in the sub_group shares the exact same
 * `usecase_goal` (treating missing values as empty strings). Used by the
 * List View to decide whether the description should render on the parent
 * task row (all same) or on the indented sub-task rows (different).
 */
export const haveSameDescription = (usecases) => {
    const list = usecases || [];
    if (list.length <= 1) return true;
    const first = list[0]?.usecase_goal || '';
    return list.every((uc) => (uc?.usecase_goal || '') === first);
};

export const getCreatedByLabel = (usecase) =>
    usecase?.created_by_user?.name ||
    usecase?.created_by_user?.email ||
    usecase?.created_by_user?.sub ||
    '';

/**
 * Profile image for the task owner, if we have one. The usecase stores the
 * creator's login-token payload (`created_by_user`), which may include a
 * `picture`. When the owner is the currently logged-in user, prefer the
 * auth-context picture — it is enriched by `/auth/me` into a persisted data
 * URL (the same image the navbar avatar shows).
 */
export const getCreatedByPicture = (usecase, currentUser) => {
    const owner = usecase?.created_by_user;
    if (!owner) return '';
    const isCurrentUser =
        !!currentUser &&
        ((owner.email && owner.email === currentUser.email) ||
            (owner.sub && owner.sub === currentUser.sub));
    if (isCurrentUser && currentUser.picture) return currentUser.picture;
    return owner.picture || '';
};

/**
 * Per-sub-task status breakdown used by the composed-task status tooltip.
 * Returns null for single-usecase tasks (the inline summary already tells
 * the full story, so no hover noise) — same pattern as the deployment
 * badge breakdown.
 */
export const getStatusBreakdown = (usecases) => {
    const list = usecases || [];
    if (list.length <= 1) return null;
    return list.map((usecase) => ({
        name: usecase?.usecase_name || 'sub-task',
        status: usecase?.status || '',
    }));
};
